import { useState } from "react";
import { ARENA, DEFAULT_STATS, RESOURCE_TABLE, SHIELD_HEAL } from "@cc/engine";
import type { ActionType, ResourceKind } from "@cc/protocol";
import type { Dict, Locale } from "./i18n";
import { actionName, resourceKindName } from "./i18n";
import { RESOURCE_STYLES } from "./theme";

/**
 * The rulebook.
 *
 * Design rule for this page: **every number is read from the engine constants,
 * and every entity drawn actually exists in the simulation.** `003.md` §7 asks
 * for a rulebook whose point is to *teach*, and a rulebook that teaches
 * something the game does not do is worse than no rulebook — the viewer learns
 * to read a lie into every match.
 *
 * Concretely, an earlier draft of the plan listed a "Portal" as an item. There
 * is no Portal in `RESOURCE_TABLE`, and there never was: it was invented from
 * memory. So this page is generated from `RESOURCE_TABLE`, `ARENA` and
 * `DEFAULT_STATS`, which means it cannot describe an entity that does not exist
 * or a number that has drifted.
 */
export function Rules({ locale, d }: { locale: Locale; d: Dict }) {
  const [expanded, setExpanded] = useState<ResourceKind | null>("trap");

  const actionRows: { action: ActionType; detail: string }[] = [
    {
      action: "MOVE",
      detail: `target [x, y] · ${DEFAULT_STATS.speed} units/tick`,
    },
    {
      action: "SCOUT",
      detail: `radius ${ARENA.scoutRadius} · costs ${ARENA.scoutTicks} ticks standing still`,
    },
    {
      action: "COLLECT",
      detail: `range ${DEFAULT_STATS.collectRange} · ${DEFAULT_STATS.collectTicks} tick channel · interrupted by damage`,
    },
    {
      action: "ATTACK",
      detail: `${DEFAULT_STATS.attackDamage} dmg · range ${DEFAULT_STATS.attackRange} · ${DEFAULT_STATS.attackCooldownTicks} tick cooldown · knockback ×${DEFAULT_STATS.knockbackSpeedMul}`,
    },
    {
      action: "DEFEND",
      detail: `−${Math.round(ARENA.defendDamageReduction * 100)}% incoming damage`,
    },
    {
      action: "FLEE",
      detail: `${ARENA.fleeSpeedMul}× speed for ${ARENA.fleeDurationTicks} ticks`,
    },
    {
      action: "EXCHANGE",
      detail: `${resourceKindName("bomb", locale)} ${ARENA.exchangeCost.bomb} / ${resourceKindName("blade", locale)} ${ARENA.exchangeCost.blade} crystal score`,
    },
    {
      action: "USE",
      detail: `${resourceKindName("bomb", locale)} AoE ${ARENA.bombDamage} · ${resourceKindName("blade", locale)} ${ARENA.bladeDamagePerTick}/tick`,
    },
    {
      action: "WAIT",
      detail: "do nothing",
    },
  ];

  return (
    <div className="page rules-page">
      <header className="page-head">
        <h1>{d.rulesTitle}</h1>
        <p className="page-sub">{d.rulesSubtitle}</p>
      </header>

      <Section title={d.rulesObjective} body={d.rulesObjectiveBody} />

      <section className="panel rules-panel">
        <div className="panel-head">
          <span>{d.rulesMap}</span>
          <span className="panel-hint">{d.rulesMapHint}</span>
        </div>
        <ArenaDiagram />
        <p className="hint">{d.rulesPhaseNote}</p>
      </section>

      <section className="panel rules-panel">
        <div className="panel-head">
          <span>{d.rulesItems}</span>
          <span className="panel-hint">{d.rulesItemHint}</span>
        </div>
        <div className="rules-items">
          {RESOURCE_TABLE.map((template) => {
            const item = itemCopy(template.kind, template.value, d);
            const open = expanded === template.kind;
            const brief = template.kind === "trap" ? d.itemLandmine : item.score;
            return (
              <button
                key={template.kind}
                className={`rules-item${open ? " open" : ""}`}
                onClick={() => setExpanded(open ? null : template.kind)}
                aria-expanded={open}
              >
                <span className="rules-item-head">
                  <span className="rules-glyph" style={{ color: item.color }}>
                    {item.glyph}
                  </span>
                  <span className="rules-item-name">
                    {resourceKindName(template.kind, locale)}
                  </span>
                  <span className="rules-item-brief">{brief}</span>
                </span>
                {open ? (
                  <ul className="rules-item-detail">
                    {item.lines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                ) : null}
              </button>
            );
          })}
        </div>
      </section>

      <section className="panel rules-panel">
        <div className="panel-head">
          <span>{d.rulesActions}</span>
        </div>
        <table className="rules-table mono">
          <tbody>
            {actionRows.map((row) => (
              <tr key={row.action}>
                <td className="rules-action-name">{actionName(row.action, locale)}</td>
                <td>{row.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <div className="rules-cols">
        <Section title={d.rulesVision} body={d.rulesVisionBody} />
        <Section title={d.rulesArena} body={d.rulesArenaBody} />
      </div>
      <Section title={d.rulesScoring} body={d.rulesScoringBody} />

      <footer className="rules-foot">
        <p className="hint">{d.rulesNumbersNote}</p>
      </footer>
    </div>
  );
}

function Section({ title, body }: { title: string; body: string }) {
  return (
    <section className="panel rules-panel">
      <div className="panel-head">
        <span>{title}</span>
      </div>
      <p className="rules-body">{body}</p>
    </section>
  );
}

/**
 * A to-scale picture of the arena.
 *
 * Worth drawing rather than describing: the single most misunderstood thing
 * about this game is that the vision radius is *small* relative to the arena.
 * A reader sees "vision radius 22" and imagines a cricket that can see most of
 * the map. Seeing a 44-unit circle inside a 100-unit square makes the whole
 * information-asymmetry design click instantly.
 */
function ArenaDiagram() {
  const size = DEFAULT_STATS.visionRadius * 2;
  const arena = 100;
  return (
    <svg className="arena-diagram" viewBox={`0 0 ${arena} ${arena}`} role="img">
      <rect x={0} y={0} width={arena} height={arena} fill="var(--panel-2)" stroke="var(--line)" />
      {/* Spawn corners. */}
      <circle cx={6} cy={6} r={2.2} fill="#ff2d55" />
      <circle cx={94} cy={94} r={2.2} fill="#8b5cf6" />
      {/* Vision circles, to scale. */}
      <circle
        cx={6}
        cy={6}
        r={DEFAULT_STATS.visionRadius}
        fill="#ff2d55"
        fillOpacity={0.08}
        stroke="#ff2d55"
        strokeOpacity={0.5}
        strokeDasharray="2 2"
      />
      <circle
        cx={94}
        cy={94}
        r={DEFAULT_STATS.visionRadius}
        fill="#8b5cf6"
        fillOpacity={0.08}
        stroke="#8b5cf6"
        strokeOpacity={0.5}
        strokeDasharray="2 2"
      />
      {/* A few resources, including one bait trap (drawn as a crystal). */}
      <circle cx={42} cy={30} r={2} fill={RESOURCE_STYLES.crystal.color} />
      <circle cx={62} cy={70} r={2} fill={RESOURCE_STYLES.energy.color} />
      <circle cx={30} cy={78} r={2} fill={RESOURCE_STYLES.shield.color} />
      <circle cx={72} cy={40} r={2} fill={RESOURCE_STYLES.crystal.color} />
      <text x={50} y={52} textAnchor="middle" fontSize={4} fill="var(--dim)">
        {size} of {arena}
      </text>
      <text x={50} y={58} textAnchor="middle" fontSize={3} fill="var(--dimmer)">
        vision diameter vs arena
      </text>
    </svg>
  );
}

function itemCopy(
  kind: ResourceKind,
  value: number,
  d: Dict,
): {
  glyph: string;
  color: string;
  score: string;
  /** Bullet lines shown when the item is expanded. */
  lines: string[];
} {
  const base = {
    glyph: RESOURCE_STYLES[kind].glyph,
    color: RESOURCE_STYLES[kind].color,
    score: d.itemScore(value),
    lines: [d.itemScore(value)],
  };
  switch (kind) {
    case "shield":
      return {
        ...base,
        lines: [d.itemScore(value), d.itemHeals(SHIELD_HEAL)],
      };
    case "bomb":
      return {
        ...base,
        score: d.itemBombBrief(ARENA.bombDamage, ARENA.bombRadius),
        lines: [
          d.itemScore(value),
          d.itemInventory,
          d.itemBombBrief(ARENA.bombDamage, ARENA.bombRadius),
          d.itemExchangeCost(ARENA.exchangeCost.bomb),
        ],
      };
    case "blade":
      return {
        ...base,
        score: d.itemBladeBrief(ARENA.bladeDamagePerTick, ARENA.bladeRange),
        lines: [
          d.itemScore(value),
          d.itemInventory,
          d.itemBladeBrief(ARENA.bladeDamagePerTick, ARENA.bladeRange),
          d.itemExchangeCost(ARENA.exchangeCost.blade),
        ],
      };
    case "trap":
      return {
        ...base,
        // A trap's table value (30) is its *bait* value, not a reward: it is
        // disguised as a high-value crystal precisely so it looks like the best
        // thing on the map. Showing "+30 score" here would be the opposite of the
        // truth, so the trap's lines describe what it actually does.
        lines: [
          d.itemDisguised,
          d.itemLandmine,
          d.itemTrapPenalty(ARENA.trapDamage, ARENA.trapScorePenalty),
        ],
      };
    default:
      return base;
  }
}
